import { useCallback, useEffect, useRef, useState } from "react";

import {
  useAnnouncementAuth,
  useAnnouncementService,
} from "../service";
import type { ReactionCounts, ReactionType } from "../types";

interface Params {
  announcementId: number;
  initialReactionCounts?: ReactionCounts;
  initialUserReaction?: ReactionType | null;
  onReactionChange?: () => void;
}

export default function useEmojiReactions({
  announcementId,
  initialReactionCounts,
  initialUserReaction,
  onReactionChange,
}: Params) {
  const service = useAnnouncementService();
  const auth = useAnnouncementAuth();
  const [counts, setCounts] = useState<ReactionCounts>(
    initialReactionCounts ?? {},
  );
  const [userReaction, setUserReaction] = useState<ReactionType | null>(
    initialUserReaction ?? null,
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasMutated, setHasMutated] = useState(false);
  const prevAuthRef = useRef(auth.isAuthenticated);

  useEffect(() => {
    const wasAuthenticated = prevAuthRef.current;
    prevAuthRef.current = auth.isAuthenticated;

    if (!wasAuthenticated && auth.isAuthenticated) {
      const controller = new AbortController();
      void service
        .fetchReactions(announcementId, controller.signal)
        .then((res) => {
          setCounts(res.reaction_counts);
          setUserReaction(res.user_reaction);
        })
        .catch(() => {});
      return () => controller.abort();
    }

    if (wasAuthenticated && !auth.isAuthenticated) {
      setUserReaction(null);
    }

    return undefined;
  }, [auth.isAuthenticated, announcementId, service]);

  useEffect(() => {
    if (!hasMutated) return;
    const controller = new AbortController();
    void service
      .fetchReactions(announcementId, controller.signal)
      .then((res) => {
        setCounts(res.reaction_counts);
        setUserReaction(res.user_reaction);
      })
      .catch(() => {
        /* swallow */
      });
    return () => controller.abort();
  }, [announcementId, hasMutated, service]);

  const handleReaction = useCallback(
    async (type: ReactionType): Promise<void> => {
      if (!auth.isAuthenticated) {
        auth.requestSignIn();
        return;
      }
      if (isSubmitting) return;

      setIsSubmitting(true);
      try {
        if (userReaction === type) {
          await service.deleteReaction(announcementId);
          setUserReaction(null);
          setCounts((prev) => ({
            ...prev,
            [type]: Math.max(0, (prev[type] ?? 0) - 1),
          }));
        } else {
          await service.upsertReaction(announcementId, type);
          setCounts((prev) => {
            const next: ReactionCounts = { ...prev };
            if (userReaction) {
              next[userReaction] = Math.max(0, (next[userReaction] ?? 0) - 1);
            }
            next[type] = (next[type] ?? 0) + 1;
            return next;
          });
          setUserReaction(type);
        }
        setHasMutated(true);
        onReactionChange?.();
      } finally {
        setIsSubmitting(false);
      }
    },
    [announcementId, auth, isSubmitting, onReactionChange, service, userReaction],
  );

  const userReactions: ReactionType[] = userReaction ? [userReaction] : [];

  return {
    userReactions,
    counts,
    isSubmitting,
    handleReaction,
  };
}
